import { useCallback, useEffect, useState } from "react";
import "./LogsPage.css";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, EmptyState } from "../components/Card";
import { PageHead } from "../components/Page";
import { hole, rich, tx } from "../i18n";

/* ══ 로그 뷰어 ══
   채택 시안 **안 A · 오류 먼저 + 출처 ②(파일)**, Figma node 29:2(사람 확정 2026-09-12).

   **핵심은 「보여준다」가 아니라 「오류를 찾아준다」다.** 로그를 그대로 흘리는 화면은
   터미널을 웹으로 옮긴 것뿐이라 값이 없다 — 그래서 위에서부터 요약 · 오류 묶음 ·
   거르개 · 전문 순이다.

   **판정은 서버가 한다**(`crates/api/src/logs.rs`) — 수준 읽기도 묶기도 거기고, 여기는
   그린다. 두 벌로 두면 갈린다(`docs/decisions/stack.md`).

   **누가 볼 수 있나**: 관리자만. 서버가 `/admin/logs` 로 막고(관리자가 아니면 403), 내비도
   관리자에게만 이 칸을 그린다 — `/connection` 과 같은 날(t-in-auth-local) 같이 닫았다
   (`docs/screens/logview.md` 「로그인이 생기는 날」 위의 얹힌 절). */

const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

export type Level = "error" | "warn" | "info" | "debug" | "trace" | "unknown";
export type ReadState = "ok" | "empty" | "missing" | "unreadable";

type ServiceState = {
  service: string;
  state: ReadState;
  total: number;
  read: number;
  shown: number;
  truncated: boolean;
  path: string;
  detail: string | null;
};
export type ContextLine = { level: Level; text: string; at: boolean };
type Group = {
  key: string;
  count: number;
  level: Level;
  service: string;
  last: string;
  /** **거르개 전 원본**의 앞뒤 20줄. 서버가 떠서 실어 준다 — 아래 주석 참고. */
  context: ContextLine[];
};
type Line = { service: string; level: Level; text: string };
type Logs = {
  dir: string;
  limit: number;
  level: string;
  services: ServiceState[];
  errorCount: number;
  warnCount: number;
  groups: Group[];
  lines: Line[];
};

const SERVICES = ["all", "api", "mcp", "naru", "web"] as const;
export const LEVELS: { key: string; label: string }[] = [
  { key: "error", label: tx("logs.level.error") },
  { key: "warn", label: tx("logs.level.warn") },
  { key: "info", label: tx("logs.all") },
];

/** 표식은 **셋**이다 — 글리프 · 글자 · 배경. 색은 그중 하나일 뿐이라, 흑백으로 봐도
 *  줄이 구분된다(시안 ③). */
export const GLYPH: Record<Level, string> = {
  error: "✕",
  warn: "△",
  info: "·",
  debug: "·",
  trace: "·",
  unknown: " ",
};

/** 서비스별 상태 칩의 문구. **넷을 안 접는다** — 사람이 할 일이 다르다(시안 ④). */
export const STATE_TEXT: Record<ReadState, string> = {
  ok: tx("logs.state.ok"),
  empty: tx("logs.state.empty"),
  missing: tx("logs.state.missing"),
  unreadable: tx("logs.state.unreadable"),
};

export function LogsPage() {
  const [service, setService] = useState<string>("all");
  const [level, setLevel] = useState<string>("warn");
  const [data, setData] = useState<Logs | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** 「앞뒤 20줄 보기」 — 오류 한 줄만 보여주면 원인이 안 보인다(시안 ②).
   *
   *  **맥락은 서버가 실어 준 `group.context` 를 그대로 쓴다.** 전에는 응답의 `lines`
   *  안에서 잘라 썼는데, 그 목록은 이미 고른 수준(기본 `warn`)으로 걸러진 것이라
   *  **오류 앞뒤의 INFO 가 통째로 빠졌다** — 원인은 대개 그 INFO 줄에 있다.
   *  여기서 다시 자르지 마라(리뷰 미달 ②, 2026-09-18). */
  const [context, setContext] = useState<Group | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const q = new URLSearchParams({ service, level });
      const r = await fetch(`${API}/admin/logs?${q}`);
      const body = await r.json();
      if (!r.ok) throw new Error(body?.message ?? `HTTP ${r.status}`);
      setData(body);
    } catch (e) {
      setData(null);
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [service, level]);

  useEffect(() => {
    void load();
  }, [load]);

  const anyUnreadable = data?.services.some((s) => s.state === "unreadable") ?? false;
  const allQuiet = data ? data.errorCount === 0 && data.warnCount === 0 : false;
  /** 읽힌 서비스가 하나도 없다. **이것만으로 「로그가 없다」로 읽으면 안 된다** —
   *  못 읽은 것도 여기 걸린다(아래 `nothingAtAll` 참고). */
  const noneReadable = data ? data.services.every((s) => s.state !== "ok") : false;
  /** **「로그가 없다」는 읽었는데 비었을 때만이다.** 못 읽은 것(권한·경로)을 여기에
   *  같이 넣으면 화면이 「아직 아무것도 안 찍혔다」고 말하는데, 사람이 할 일은 정반대다
   *  — 서비스가 도는지 보는 대신 경로와 권한을 봐야 한다. 실기동에서 로그 파일의
   *  권한을 막아 봤더니 위의 ✕ 경고와 아래의 ○ 빈 상태가 **서로 반대되는 말을 같이**
   *  하고 있었다(2026-09-25). 셋을 가르는 것이 이 화면의 수용 기준이다. */
  const nothingAtAll = noneReadable && !anyUnreadable;
  /** 읽힌 것이 하나도 없는데 못 읽은 것이 있다 — 빈 자리가 **그 이유**를 말해야 한다.
   *  읽힌 서비스가 하나라도 있으면 전문이 그려지므로 위의 경고 한 줄로 충분하다. */
  const nothingReadableButBlocked = noneReadable && anyUnreadable;

  return (
    <AppShell route="/logs">
      <PageHead
        title={tx("nav.logs")}
        lead={
          <>
            <p>{tx("logs.lead1")}</p>
            <p>
              {rich(tx("logs.lead2", hole("find", "cmd", "socket", "admin")), {
                find: <b>{tx("logs.lead2Find")}</b>,
                cmd: <code>make logs</code>,
                socket: <b>{tx("logs.lead2Socket")}</b>,
                admin: <b>{tx("logs.lead2Admin")}</b>,
              })}
            </p>
          </>
        }
      />

      {/* ── ① 요약 줄 ─────────────────────────────────────────────────────── */}
      {err ? (
        <Alert tone="error">
          <span aria-hidden="true">✕</span>{" "}
          {rich(tx("logs.callFailed", { err, ...hole("head") }), { head: <b>{tx("logs.callFailedHead")}</b> })}
        </Alert>
      ) : data ? (
        <div className="logs__summary">
          <span className={data.errorCount ? "logs__count logs__count--error" : "logs__count"}>
            <span aria-hidden="true">{data.errorCount ? "✕" : "✓"}</span> {tx("logs.errors", { n: data.errorCount })}
          </span>
          <span className={data.warnCount ? "logs__count logs__count--warn" : "logs__count"}>
            <span aria-hidden="true">△</span> {tx("logs.warns", { n: data.warnCount })}
          </span>
          <span className="git__spacer" />
          {data.services.map((s) => (
            <span
              key={s.service}
              className={`logs__chip logs__chip--${s.state}`}
              title={`${s.path}${s.detail ? ` — ${s.detail}` : ""}`}
            >
              {s.service} · {STATE_TEXT[s.state]}
              {s.state === "ok" ? ` ${tx("chat.lines", { n: s.total })}` : ""}
            </span>
          ))}
          <Button size="sm" onClick={() => void load()} loading={loading}>
            {tx("tidy.reload")}
          </Button>
        </div>
      ) : null}

      {/* 읽기 실패는 **요약과 따로** 크게 말한다 — 「오류 0」과 겹쳐 보이면
          「조용하다」로 잘못 읽힌다. */}
      {anyUnreadable ? (
        <Alert tone="error">
          <span aria-hidden="true">✕</span>{" "}
          {rich(tx("logs.someUnreadable", hole("head")), { head: <b>{tx("logs.someUnreadableHead")}</b> })}
        </Alert>
      ) : null}

      {/* ── ② 오류 묶음 ───────────────────────────────────────────────────── */}
      {data && data.groups.length ? (
        <section className="logs__groups" aria-label={tx("logs.groups")}>
          <h2 className="logs__h2">{tx("logs.groups")}</h2>
          <ul className="logs__list">
            {data.groups.map((g) => (
              <li key={`${g.service}:${g.key}`} className={`logs__group logs__group--${g.level}`}>
                <span className="logs__glyph" aria-hidden="true">
                  {GLYPH[g.level]}
                </span>
                <span className="logs__level">{g.level.toUpperCase()}</span>
                <span className="logs__service">{g.service}</span>
                <span className="logs__last">{g.last}</span>
                <span className="git__spacer" />
                <span className="logs__times">{tx("chat.stage.times", { n: g.count })}</span>
                <button
                  type="button"
                  className="b-btn b-btn-secondary b-btn-sm logs__ctxbtn"
                  onClick={() => setContext(context?.key === g.key ? null : g)}
                >
                  {tx(context?.key === g.key ? "logs.ctxClose" : "logs.ctxOpen")}
                </button>
              </li>
            ))}
          </ul>
          {context ? (
            <pre className="logs__pre logs__pre--ctx">
              {context.context.map((l, i) => (
                <div
                  key={i}
                  className={
                    l.at
                      ? `logs__line logs__line--${l.level} logs__line--at`
                      : `logs__line logs__line--${l.level}`
                  }
                >
                  <span aria-hidden="true">{l.at ? "▸" : GLYPH[l.level]}</span> {l.text}
                </div>
              ))}
            </pre>
          ) : null}
        </section>
      ) : null}

      {/* ── ③ 거르개 ─────────────────────────────────────────────────────── */
       /* **오류 묶음 뒤에 온다.** 채택안(Figma 29:2)이 「요약 → 오류 묶음 → 거르개 →
          로그 전문」으로 정한 것은 마감이 아니라 **오류를 먼저 보게 하려는 배치**다 —
          거르개가 위에 있으면 사람이 무엇을 거를지부터 고르게 된다(리뷰 미달 ①). */}
      <div className="logs__filters">
        <div className="logs__seg" role="group" aria-label={tx("logs.service")}>
          {SERVICES.map((s) => (
            <button
              key={s}
              type="button"
              className={`b-chip${service === s ? " is-on" : ""}`}
              aria-pressed={service === s}
              onClick={() => setService(s)}
            >
              {s === "all" ? tx("logs.all") : s}
            </button>
          ))}
        </div>
        <div className="logs__seg" role="group" aria-label={tx("logs.levelLabel")}>
          {LEVELS.map((l) => (
            <button
              key={l.key}
              type="button"
              className={`b-chip${level === l.key ? " is-on" : ""}`}
              aria-pressed={level === l.key}
              onClick={() => setLevel(l.key)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>


      {/* ── ④ 빈 상태 셋 — 셋 다 사람이 할 일이 다르다 ───────────────────── */}
      {data && nothingReadableButBlocked ? (
        <EmptyState
          title={`✕ ${tx("logs.empty.blocked")}`}
          desc={tx("logs.empty.blockedDesc")}
        />
      ) : data && nothingAtAll ? (
        <EmptyState
          title={`○ ${tx("logs.empty.none")}`}
          desc={tx("logs.empty.noneDesc")}
        />
      ) : data && allQuiet && data.groups.length === 0 ? (
        <EmptyState title={`✓ ${tx("logs.empty.quiet")}`} desc={tx("logs.empty.quietDesc")} />
      ) : null}

      {/* ── ⑤ 로그 전문 ───────────────────────────────────────────────────── */}
      {data && data.lines.length ? (
        <section className="logs__full" aria-label={tx("logs.full")}>
          <h2 className="logs__h2">{tx("logs.full")}</h2>
          {/* **자른 것을 잘랐다고 적는다.** 조용히 자르면 그게 전부인 줄 안다. */}
          <p className="caption">
            {data.services
              .filter((s) => s.state === "ok")
              .map((s) =>
                s.truncated
                  ? tx("logs.truncated", { service: s.service, total: s.total, read: s.read })
                  : tx("logs.whole", { service: s.service, total: s.total }),
              )
              .join(" · ")}
          </p>
          <pre className="logs__pre">
            {data.lines.map((l, i) => (
              <div key={i} className={`logs__line logs__line--${l.level}`}>
                <span aria-hidden="true">{GLYPH[l.level]}</span>{" "}
                <span className="logs__service">{l.service}</span> {l.text}
              </div>
            ))}
          </pre>
        </section>
      ) : null}
    </AppShell>
  );
}
